import {
  addressFormValues,
  firstAddressError,
  isEmptyAddress,
  validateAddressFields,
  type AddressFieldErrors,
  type AddressFormValues,
  type AddressLikeForValidation,
  type ManualAddressFormValues,
} from '@/lib/address-form-validation';

/**
 * Which address the estimate form should validate, and what it found. Both
 * halves live together because the form has three sources per address (a saved
 * contact address, typed manual fields, or a bill-to contact that carries its
 * own) and only one of them is live at a time — picking the wrong one is how a
 * blank untouched address ended up blocking save.
 */
export interface EstimateAddressValidationInput {
  addressMode: 'select' | 'manual';
  selectedAddressIndex: number | null;
  manualServiceAddress: ManualAddressFormValues;
  billingMode: 'select' | 'manual';
  selectedBillingIndex: number | null;
  manualBillingAddress: ManualAddressFormValues;
  /** A chosen bill-to contact owns the billing address, so the form's fields aren't in play. */
  billToId?: string | null;
  contactAddresses: AddressLikeForValidation[];
}

export interface EstimateAddressValidation {
  serviceAddressErrors: AddressFieldErrors;
  billingAddressErrors: AddressFieldErrors;
  /** First error across both, already labeled; null when there is nothing to fix. */
  addressValidationError: string | null;
}

/**
 * An untouched manual address is not an invalid one: the payload omits a manual
 * address unless all four fields are filled, and the server treats it as
 * optional. Validate only once the user has typed something.
 */
function manualValidationTarget(values: ManualAddressFormValues): AddressFormValues | null {
  return isEmptyAddress(values) ? null : values;
}

function selectedValidationTarget(
  index: number | null,
  addresses: AddressLikeForValidation[],
): AddressFormValues | null {
  if (index === null) return null;
  const address = addresses[index];
  return address ? addressFormValues(address) : null;
}

export function resolveEstimateAddressValidation(
  input: EstimateAddressValidationInput,
): EstimateAddressValidation {
  const serviceTarget =
    input.addressMode === 'manual'
      ? manualValidationTarget(input.manualServiceAddress)
      : selectedValidationTarget(input.selectedAddressIndex, input.contactAddresses);
  const billingTarget =
    typeof input.billToId === 'string' && input.billToId
      ? null
      : input.billingMode === 'manual'
        ? manualValidationTarget(input.manualBillingAddress)
        : selectedValidationTarget(input.selectedBillingIndex, input.contactAddresses);

  const serviceAddressErrors = serviceTarget ? validateAddressFields(serviceTarget) : {};
  const billingAddressErrors = billingTarget ? validateAddressFields(billingTarget) : {};

  return {
    serviceAddressErrors,
    billingAddressErrors,
    addressValidationError:
      firstAddressError('Service address', serviceAddressErrors) ??
      firstAddressError('Billing address', billingAddressErrors),
  };
}
